import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  DollarSign, 
  Send,
  Plus,
  Phone,
  Mail,
  Building,
  Clock,
  Check,
  AlertCircle,
  ChevronRight,
  Calendar,
  Edit3,
  Percent,
  Gift,
  Upload,
  Image,
  Eye,
  CheckCircle,
  X,
  Receipt,
  Loader2
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BoothManagement = ({ token }) => {
  const [renters, setRenters] = useState([]);
  const [payments, setPayments] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarSummary, setCalendarSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddRenter, setShowAddRenter] = useState(false);
  const [showEditRenter, setShowEditRenter] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [selectedRenter, setSelectedRenter] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [activeTab, setActiveTab] = useState("renters"); // renters, calendar, payments
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const receiptFileRef = useRef(null);
  
  const [newRenter, setNewRenter] = useState({
    renter_name: "",
    renter_email: "",
    renter_phone: "",
    booth_number: "",
    rent_amount: "",
    payment_due_day: 1
  });

  const [editRenter, setEditRenter] = useState({
    renter_id: "",
    renter_name: "",
    renter_email: "",
    renter_phone: "",
    booth_number: "",
    rent_amount: "",
    payment_due_day: 1,
    discount_percent: 0,
    discount_amount: 0,
    is_free_rent: false,
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rentersRes, paymentsRes, calendarRes] = await Promise.all([
        axios.get(`${API}/booth/renters`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/booth/rent-payments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/booth/rent-calendar`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRenters(rentersRes.data);
      setPayments(paymentsRes.data);
      setCalendarEvents(calendarRes.data.events || []);
      setCalendarSummary(calendarRes.data.summary || {});
    } catch (error) {
      console.error("Failed to fetch booth data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addRenter = async () => {
    try {
      await axios.post(`${API}/booth/renters`, {
        ...newRenter,
        renter_user_id: `user_${Date.now()}`,
        rent_amount: parseFloat(newRenter.rent_amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booth renter added!");
      setShowAddRenter(false);
      setNewRenter({
        renter_name: "",
        renter_email: "",
        renter_phone: "",
        booth_number: "",
        rent_amount: "",
        payment_due_day: 1
      });
      fetchData();
    } catch (error) {
      console.error("Add renter error:", error.response?.data || error);
      toast.error(error.response?.data?.detail || "Failed to add renter");
    }
  };

  const updateRenter = async () => {
    try {
      // Update basic info
      await axios.put(`${API}/booth/renters/${editRenter.renter_id}`, {
        renter_user_id: editRenter.renter_user_id || `user_${Date.now()}`,
        renter_name: editRenter.renter_name,
        renter_email: editRenter.renter_email,
        renter_phone: editRenter.renter_phone,
        booth_number: editRenter.booth_number,
        rent_amount: parseFloat(editRenter.rent_amount),
        payment_due_day: editRenter.payment_due_day
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update discount separately
      await axios.put(
        `${API}/booth/renters/${editRenter.renter_id}/discount?discount_percent=${editRenter.discount_percent}&discount_amount=${editRenter.discount_amount}&is_free_rent=${editRenter.is_free_rent}&notes=${encodeURIComponent(editRenter.notes || '')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Renter updated!");
      setShowEditRenter(false);
      fetchData();
    } catch (error) {
      console.error("Update renter error:", error);
      toast.error("Failed to update renter");
    }
  };

  const openEditRenter = (renter) => {
    setEditRenter({
      renter_id: renter.renter_id,
      renter_user_id: renter.renter_user_id,
      renter_name: renter.renter_name,
      renter_email: renter.renter_email,
      renter_phone: renter.renter_phone || "",
      booth_number: renter.booth_number || "",
      rent_amount: renter.rent_amount,
      payment_due_day: renter.payment_due_day || 1,
      discount_percent: renter.discount_percent || 0,
      discount_amount: renter.discount_amount || 0,
      is_free_rent: renter.is_free_rent || false,
      notes: renter.notes || ""
    });
    setShowEditRenter(true);
  };

  const createPaymentRequest = async (renterId, amount) => {
    try {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      
      const response = await axios.post(
        `${API}/booth/rent-payment?renter_id=${renterId}&amount=${amount}&due_date=${dueDate.toISOString().split('T')[0]}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment request created!");
      fetchData();
      return response.data;
    } catch (error) {
      toast.error("Failed to create payment request");
    }
  };

  const sendPaymentLink = async (paymentId) => {
    try {
      const response = await axios.post(`${API}/booth/send-payment-link/${paymentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to send payment link");
    }
  };

  const markAsPaid = async (paymentId, paymentMethod = "manual") => {
    try {
      await axios.put(
        `${API}/booth/rent-payment/${paymentId}/mark-paid?payment_method=${paymentMethod}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment marked as paid!");
      setShowReceiptModal(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to mark payment as paid");
    }
  };

  const handleReceiptUpload = async (e, paymentId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('notes', 'Receipt uploaded by owner');

      await axios.post(
        `${API}/booth/rent-payment/${paymentId}/upload-receipt`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success("Receipt uploaded!");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload receipt");
    } finally {
      setUploadingReceipt(false);
      if (e.target) e.target.value = '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "pending_review": return "bg-blue-100 text-blue-700";
      case "overdue": return "bg-red-100 text-red-700";
      case "sent_reminder": return "bg-purple-100 text-purple-700";
      case "not_created": return "bg-gray-100 text-gray-500";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "paid": return "Paid";
      case "pending": return "Pending";
      case "pending_review": return "Receipt Uploaded";
      case "overdue": return "Overdue";
      case "sent_reminder": return "Reminder Sent";
      case "not_created": return "Not Invoiced";
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const totalRentExpected = renters.reduce((sum, r) => {
    if (r.is_free_rent) return sum;
    let amount = r.rent_amount || 0;
    if (r.discount_percent > 0) amount *= (1 - r.discount_percent / 100);
    if (r.discount_amount > 0) amount = Math.max(0, amount - r.discount_amount);
    return sum + amount;
  }, 0);

  const totalRentPaid = payments.filter(p => p.status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-purple-100 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs">Active Renters</span>
            </div>
            <p className="text-3xl font-bold">{renters.length}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-100 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">Monthly Expected</span>
            </div>
            <p className="text-3xl font-bold">{formatCurrency(totalRentExpected)}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-100 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs">Paid This Month</span>
            </div>
            <p className="text-3xl font-bold">{calendarSummary.paid_this_month || 0}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-amber-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-100 mb-1">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">Pending</span>
            </div>
            <p className="text-3xl font-bold">{calendarSummary.pending_this_month || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        <Button
          variant={activeTab === "renters" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("renters")}
          className={activeTab === "renters" ? "bg-purple-500" : ""}
        >
          <Users className="w-4 h-4 mr-1" />
          Renters
        </Button>
        <Button
          variant={activeTab === "calendar" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("calendar")}
          className={activeTab === "calendar" ? "bg-purple-500" : ""}
        >
          <Calendar className="w-4 h-4 mr-1" />
          Calendar
        </Button>
        <Button
          variant={activeTab === "payments" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("payments")}
          className={activeTab === "payments" ? "bg-purple-500" : ""}
        >
          <Receipt className="w-4 h-4 mr-1" />
          Payments
        </Button>
      </div>

      {/* Renters Tab */}
      {activeTab === "renters" && (
        <>
          {/* Add Renter Button */}
          <Dialog open={showAddRenter} onOpenChange={setShowAddRenter}>
            <DialogTrigger asChild>
              <Button className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
                <Plus className="w-5 h-5 mr-2" />
                Add Booth Renter
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Booth Renter</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newRenter.renter_name}
                    onChange={(e) => setNewRenter({...newRenter, renter_name: e.target.value})}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newRenter.renter_email}
                    onChange={(e) => setNewRenter({...newRenter, renter_email: e.target.value})}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newRenter.renter_phone}
                    onChange={(e) => setNewRenter({...newRenter, renter_phone: e.target.value})}
                    placeholder="+1 555 123 4567"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Booth/Suite Number</Label>
                    <Input
                      value={newRenter.booth_number}
                      onChange={(e) => setNewRenter({...newRenter, booth_number: e.target.value})}
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <Label>Monthly Rent ($)</Label>
                    <Input
                      type="number"
                      value={newRenter.rent_amount}
                      onChange={(e) => setNewRenter({...newRenter, rent_amount: e.target.value})}
                      placeholder="800"
                    />
                  </div>
                </div>
                <div>
                  <Label>Payment Due Day (1-28)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={newRenter.payment_due_day}
                    onChange={(e) => setNewRenter({...newRenter, payment_due_day: parseInt(e.target.value) || 1})}
                  />
                </div>
                <Button 
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white"
                  onClick={addRenter}
                >
                  Add Renter
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Renter Dialog */}
          <Dialog open={showEditRenter} onOpenChange={setShowEditRenter}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Edit Booth Renter</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editRenter.renter_name}
                    onChange={(e) => setEditRenter({...editRenter, renter_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editRenter.renter_email}
                    onChange={(e) => setEditRenter({...editRenter, renter_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editRenter.renter_phone}
                    onChange={(e) => setEditRenter({...editRenter, renter_phone: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Booth/Suite #</Label>
                    <Input
                      value={editRenter.booth_number}
                      onChange={(e) => setEditRenter({...editRenter, booth_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Monthly Rent ($)</Label>
                    <Input
                      type="number"
                      value={editRenter.rent_amount}
                      onChange={(e) => setEditRenter({...editRenter, rent_amount: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Payment Due Day (1-28)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={editRenter.payment_due_day}
                    onChange={(e) => setEditRenter({...editRenter, payment_due_day: parseInt(e.target.value) || 1})}
                  />
                </div>

                {/* Discount Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-purple-500" />
                    Discounts & Free Rent
                  </h4>
                  
                  <div className="flex items-center justify-between mb-4">
                    <Label className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-green-500" />
                      Free Rent
                    </Label>
                    <Switch
                      checked={editRenter.is_free_rent}
                      onCheckedChange={(checked) => setEditRenter({...editRenter, is_free_rent: checked})}
                    />
                  </div>

                  {!editRenter.is_free_rent && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Discount %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={editRenter.discount_percent}
                          onChange={(e) => setEditRenter({...editRenter, discount_percent: parseFloat(e.target.value) || 0})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label>Or Fixed Discount ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editRenter.discount_amount}
                          onChange={(e) => setEditRenter({...editRenter, discount_amount: parseFloat(e.target.value) || 0})}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={editRenter.notes}
                    onChange={(e) => setEditRenter({...editRenter, notes: e.target.value})}
                    placeholder="Internal notes about this renter..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditRenter(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white"
                  onClick={updateRenter}
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Renters List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-purple-500" />
                Booth Renters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {renters.map((renter) => (
                  <div key={renter.renter_id} className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold">{renter.renter_name}</h4>
                          {renter.is_free_rent && (
                            <Badge className="bg-green-100 text-green-700 text-xs">FREE</Badge>
                          )}
                          {!renter.is_free_rent && renter.discount_percent > 0 && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">{renter.discount_percent}% OFF</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">Suite #{renter.booth_number || "N/A"}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-purple-600">
                          {renter.is_free_rent ? (
                            <span className="text-green-600">Free</span>
                          ) : (
                            formatCurrency(
                              renter.discount_percent > 0 
                                ? renter.rent_amount * (1 - renter.discount_percent / 100)
                                : renter.rent_amount - (renter.discount_amount || 0)
                            )
                          )}
                        </p>
                        {!renter.is_free_rent && (renter.discount_percent > 0 || renter.discount_amount > 0) && (
                          <p className="text-xs text-gray-400 line-through">{formatCurrency(renter.rent_amount)}</p>
                        )}
                        <p className="text-xs text-gray-500">Due day {renter.payment_due_day}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {renter.renter_email}
                      </span>
                      {renter.renter_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {renter.renter_phone}
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openEditRenter(renter)}
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => createPaymentRequest(renter.renter_id, renter.rent_amount)}
                      >
                        <DollarSign className="w-4 h-4 mr-1" />
                        Create Invoice
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white"
                        onClick={() => {
                          const pendingPayment = payments.find(p => p.renter_id === renter.renter_id && p.status !== "paid");
                          if (pendingPayment) {
                            sendPaymentLink(pendingPayment.payment_id);
                          } else {
                            toast.info("Create an invoice first");
                          }
                        }}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Send Reminder
                      </Button>
                    </div>
                  </div>
                ))}
                {renters.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Building className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No booth renters yet</p>
                    <p className="text-sm">Add your first renter to start collecting rent</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Calendar Tab */}
      {activeTab === "calendar" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              Rent Due Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {calendarEvents.map((event, idx) => {
                const isPast = new Date(event.due_date) < new Date();
                const isToday = event.due_date === new Date().toISOString().split('T')[0];
                
                return (
                  <div 
                    key={`${event.renter_id}-${event.due_date}-${idx}`}
                    className={`p-4 rounded-lg border ${
                      isToday ? 'border-purple-500 bg-purple-50' :
                      isPast && event.payment_status !== 'paid' ? 'border-red-300 bg-red-50' :
                      'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{event.renter_name}</p>
                          {event.is_free_rent && (
                            <Badge className="bg-green-100 text-green-700 text-xs">FREE</Badge>
                          )}
                          {isToday && (
                            <Badge className="bg-purple-500 text-white text-xs">TODAY</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          Suite #{event.booth_number || "N/A"} • Due: {new Date(event.due_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div>
                          <p className="font-bold">{formatCurrency(event.final_amount)}</p>
                          <Badge className={getStatusColor(event.payment_status)}>
                            {getStatusLabel(event.payment_status)}
                          </Badge>
                        </div>
                        {event.payment_status === "pending_review" && event.receipt_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPayment(event);
                              setShowReceiptModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        )}
                        {event.payment_status !== "paid" && event.payment_id && (
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                            onClick={() => markAsPaid(event.payment_id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {calendarEvents.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No rent due dates</p>
                  <p className="text-sm">Add renters to see their payment schedule</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments Tab */}
      {activeTab === "payments" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-500" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {payments.map((payment) => {
                const renter = renters.find(r => r.renter_id === payment.renter_id);
                return (
                  <div key={payment.payment_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{renter?.renter_name || "Unknown"}</p>
                      <p className="text-sm text-gray-500">
                        Due: {payment.due_date} 
                        {payment.paid_date && ` • Paid: ${new Date(payment.paid_date).toLocaleDateString()}`}
                      </p>
                      {payment.payment_method && (
                        <p className="text-xs text-gray-400 capitalize">via {payment.payment_method}</p>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className="font-bold">{formatCurrency(payment.amount)}</p>
                        <Badge className={getStatusColor(payment.status)}>
                          {getStatusLabel(payment.status)}
                        </Badge>
                      </div>
                      
                      {/* Receipt Review Button */}
                      {payment.status === "pending_review" && payment.receipt_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowReceiptModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review Receipt
                        </Button>
                      )}
                      
                      {/* Mark as Paid */}
                      {payment.status !== "paid" && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                            onClick={() => markAsPaid(payment.payment_id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {payments.length === 0 && (
                <p className="text-center py-4 text-gray-500">No payment history</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipt Review Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-500" />
              Review Payment Receipt
            </DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="font-bold text-lg">{formatCurrency(selectedPayment.amount || selectedPayment.final_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due Date</p>
                  <p className="font-medium">{selectedPayment.due_date}</p>
                </div>
              </div>
              
              {selectedPayment.receipt_url && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Payment Screenshot</p>
                  <img 
                    src={`${API.replace('/api', '')}${selectedPayment.receipt_url}`}
                    alt="Payment receipt"
                    className="w-full rounded-lg border max-h-64 object-contain bg-gray-100"
                  />
                </div>
              )}
              
              {selectedPayment.receipt_notes && (
                <div>
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm">{selectedPayment.receipt_notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>
              <X className="w-4 h-4 mr-1" />
              Close
            </Button>
            <Select onValueChange={(method) => markAsPaid(selectedPayment?.payment_id, method)}>
              <SelectTrigger className="w-[180px] bg-green-500 text-white border-0">
                <Check className="w-4 h-4 mr-1" />
                Mark as Paid
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cashapp">CashApp</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="zelle">Zelle</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoothManagement;
